<template>
    <v-container fluid>
      <v-card elevation="2">
        <v-card-title class="d-flex align-center pe-2 tableheader white--text">
          <v-icon left color="white" class="mr-2">mdi-account-multiple-outline</v-icon>
          User Roles Management
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            density="compact"
            label="Search Users (Email)"
            prepend-inner-icon="mdi-magnify"
            variant="solo-filled"
            flat
            hide-details
            single-line
            class="me-2 search-field"
          ></v-text-field>

          <v-btn 
          color="primary" 
          @click="openAddUserDialog" 
          :loading="dialogLoading"
          v-if="authStore.canEdit"
        >
          <v-icon left>mdi-plus</v-icon> Add User
        </v-btn>
        </v-card-title>
        <v-divider></v-divider>
  
        <v-data-table
          :headers="headers"
          :items="users"
          :search="search"
          item-value="id"
          class="elevation-1"
          :loading="tableLoading"
          loading-text="Loading users... Please wait"
        >

        <template v-slot:[`item.company_id`]="{ value, item }">
            <div>
              {{ (() => { console.log('Template called with value:', value, 'item:', item); return value ? findName(companies, value) : '�'; })() }}
              <!-- <small class="text-caption d-block">Debug: value={{ value }}, item.company_id={{ item.company_id }}</small>
              <small class="text-caption d-block">All item keys: {{ Object.keys(item).join(', ') }}</small> -->
            </div>
          </template>

          <template v-slot:[`item.roles`]="{ item }">
            <v-chip
              v-for="role in item.roles"
              :key="role"
              color="primary"
              label
              small
              class="me-1 my-1"
            >
              {{ getRoleName(role) }}
            </v-chip>
            <span v-if="!item.roles || item.roles.length === 0" class="text-disabled">No roles</span>
          </template>
  
          <template v-slot:[`item.active`]="{ value }">
              <v-chip :color="value ? 'success' : 'error'" label small>
                  {{ value ? 'Active' : 'Inactive' }}
              </v-chip>
          </template>
  
          <template v-slot:[`item.country_code`]="{ value }">
            {{ findCountryName(value) }}
          </template>

          <template v-slot:[`item.actions`]="{ item }">
            <v-tooltip location="top" text="Edit Roles">
              <template v-slot:activator="{ props }">
                  <v-btn icon="mdi-shield-edit-outline" v-bind="props" variant="text" color="secondary" @click="openEditRolesDialog(item)" 
                  size="small"></v-btn>
              </template>
            </v-tooltip>

            <!-- Reset password -->
            <v-tooltip text="Reset password">
              <template #activator="{ props }">
                <v-btn icon="mdi-lock-reset" v-bind="props" variant="text" color="secondary" size="small"
                        @click="openPasswordDlg(item)"
                        v-if="authStore.isAdmin">
                </v-btn>
              </template>
            </v-tooltip>

            <!-- Disable / Enable user -->
            <!-- <v-tooltip :text="item.active ? 'Disable user' : 'Enable user'">
              <template #activator="{ props }">
                <v-btn :icon="item.active ? 'mdi-account-off' : 'mdi-account-check'" v-bind="props" variant="text" color="secondary" size="small"
                        @click="confirmToggleActive(item)"
                        v-if="authStore.isAdmin">
                </v-btn>
              </template>
            </v-tooltip> -->

            <!-- Edit profile -->
            <v-tooltip text="Edit profile">
              <template #activator="{ props }">
                <v-btn icon="mdi-account-edit" v-bind="props" variant="text" color="secondary" size="small"
                        @click="openProfileDialog(item)"
                        v-if="authStore.isAdmin">
                </v-btn>
              </template>
            </v-tooltip>

          </template>
  
          <template v-slot:no-data>
            <v-alert :value="true" color="info" icon="mdi-information-variant" class="ma-3">
              {{ tableLoading ? 'Fetching users...' : 'No users found.' }}
            </v-alert>
          </template>
        </v-data-table>
      </v-card>
  
      <v-dialog v-model="editDialog" max-width="500px" persistent>
        <v-card :loading="dialogLoading">
          <v-card-title class="tableheader white--text">
            <span class="text-h5">Edit Roles for {{ editingUser?.email }}</span>
          </v-card-title>
          <v-card-text class="mt-4">
            <v-form ref="rolesForm">
              <v-select
                v-model="selectedUserRoles"
                :items="allAvailableRoles"
                item-title="readable_name"
                item-value="name" 
                label="Assign Roles"
                multiple
                chips
                closable-chips
                variant="outlined"
                :loading="loadingAllRoles"
                :rules="[rules.minOneRole]"
              ></v-select>

            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey-darken-1" variant="text" @click="closeEditRolesDialog" :disabled="dialogLoading">Cancel</v-btn>
            <v-btn color="primary" variant="elevated" @click="saveUserRoles" :loading="dialogLoading">Save Roles</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <v-dialog v-model="addUserDialog" max-width="500" max-height="750">
        <v-card>
          <v-card-title>Add User</v-card-title>
          <v-card-text>
            <v-form ref="addUserForm" v-model="isFormValid" @submit.prevent="handleAddUser">
                <v-text-field
                  v-model="form.email"
                  label="Email Address"
                  prepend-inner-icon="mdi-email-outline"
                  variant="outlined"
                  type="email"
                  :rules="[rules.required, rules.email]"
                  required
                  class="mb-4"
                  color="primary"
                  @input="authStore.clearError()"
                ></v-text-field>

                <v-text-field
                  v-model="form.first_name"
                  label="First Name"
                  prepend-inner-icon="mdi-account-outline"
                  variant="outlined"
                  type="text"
                  :rules="[rules.required]"
                />

                <v-text-field
                  v-model="form.last_name"
                  label="Last Name"
                  prepend-inner-icon="mdi-account-outline"
                  variant="outlined"
                  type="text"
                  :rules="[rules.required]"
                />

                <v-select
              :items="companies"
              item-title="name"
              item-value="id"
              v-model="form.company_id"
              label="Company*"
              variant="outlined"
              class="mb-2"
              :rules="[rules.required]"
            />

            <v-select
              :items="countries"
              item-title="country_name"
              item-value="country_code"
              v-model="form.country_code"
              label="Country"
              variant="outlined"
              class="mb-2"
              :rules="[rules.required]"
            />
  
                <v-text-field
                  v-model="form.password"
                  label="Password"
                  prepend-inner-icon="mdi-lock-outline"
                  variant="outlined"
                  :type="showPassword ? 'text' : 'password'"
                  :append-inner-icon="showPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                  @click:append-inner="showPassword = !showPassword"
                  :rules="[rules.required, rules.minLength(8)]"
                  required
                  class="mb-4"
                  color="primary"
                  hint="Minimum 8 characters"
                  @input="authStore.clearError()"
                ></v-text-field>
  
                <v-text-field
                  v-model="form.confirmPassword"
                  label="Confirm Password"
                  prepend-inner-icon="mdi-lock-check-outline"
                  variant="outlined"
                  :type="showConfirmPassword ? 'text' : 'password'"
                  :append-inner-icon="showConfirmPassword ? 'mdi-eye-off-outline' : 'mdi-eye-outline'"
                  @click:append-inner="showConfirmPassword = !showConfirmPassword"
                  :rules="[rules.required, rules.passwordMatch]"
                  required
                  class="mb-6"
                  color="primary"
                  @input="authStore.clearError()"
                ></v-text-field>
  
                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="addUserDialog = false">Cancel</v-btn>
                  <v-btn color="primary" variant="elevated" :loading="dialogLoading" :disabled="!isFormValid" type="submit">Save</v-btn>
                </v-card-actions>
              </v-form>
          </v-card-text>
        </v-card>
      </v-dialog>

      <!-- Password-reset dialog -->
      <v-dialog v-model="pwdDlg.show" max-width="420">
        <v-card>
          <v-card-title>Reset password for {{ pwdDlg.user?.email }}</v-card-title>
          <v-card-text>
            <v-form ref="pwdForm" v-model="pwdDlg.valid">
              <v-text-field v-model="pwdDlg.newPwd"
                            type="password" label="New password*" variant="outlined"
                            :rules="[rules.req, rules.pwdStrength]"/>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-spacer/>
            <v-btn text @click="pwdDlg.show = false">Cancel</v-btn>
            <v-btn color="primary" :disabled="!pwdDlg.valid"
                  @click="submitPassword">Save</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>


      <v-dialog v-model="profileDialog" max-width="500">
        <v-card>
          <v-card-title>Edit Profile</v-card-title>
          <v-form ref="profileForm" v-model="isFormValid" @submit.prevent="saveProfile">
          <v-card-text>
            
            <v-text-field
              type="text"
              v-model="editingUser.email"
              label="Email*"
              class="mb-2"
              variant="outlined"
              :rules="[editrules.required]"
            />

            <v-select
              :items="companies"
              item-title="name"
              item-value="id"
              v-model="editingUser.company_id"
              label="Company*"
              variant="outlined"
              class="mb-2"
              :rules="[editrules.required]"
            />
  
            <v-text-field
              type="text"
              v-model="editingUser.first_name"
              label="First Name*"
              class="mb-2"
              variant="outlined"
              :rules="[editrules.required]"
            />

            <v-text-field
              type="text"
              v-model="editingUser.last_name"
              label="Last Name*"
              class="mb-2"
              variant="outlined"
              :rules="[editrules.required]"
            />

            <v-select
              :items="countries"
              item-title="country_name"
              item-value="country_code"
              v-model="editingUser.country_code"
              label="Country"
              variant="outlined"
              class="mb-2"
              :rules="[editrules.required]"
            />

            <v-select
              v-model="editingUser.active"
              :items="[{name: 'Active', id: true}, {name: 'Inactive', id: false}]"
              item-title="name"
              item-value="id"
              label="Active"
              class="mb-2"
              variant="outlined"
            />
          </v-card-text>
          <v-divider></v-divider>
        <v-card-actions class="bg-grey-lighten-4 pa-3">
          <v-spacer></v-spacer>
          <!-- <v-btn color="primary" variant="tonal" @click="closeProfileDialog">Close</v-btn> -->
          <v-btn text @click="profileDialog=false">Cancel</v-btn>
          <v-btn color="primary" type="submit" variant="elevated" :disabled="!isFormValid">Save</v-btn>
        </v-card-actions>
          </v-form>
        </v-card>
      </v-dialog>
      

      <!-- Disable confirmation -->
      <v-dialog v-model="disableDlg.show" max-width="450px">
        <v-card>
          <v-card-title class="text-h6">
            {{ disableDlg.user?.active ? 'Disable' : 'Re-enable' }} account?
          </v-card-title>
          <v-card-text>
            You are about to {{ disableDlg.user?.active ? 'disable' : 'enable' }}
            <strong>{{ disableDlg.user?.email }}</strong>.
          </v-card-text>
          <v-card-actions>
            <v-spacer/>
            <v-btn color="grey-darken-1" variant="text" @click="disableDlg.show = false">Cancel</v-btn>
            <v-btn :color="disableDlg.user?.active ? 'error' : 'success'" variant="elevated"
                  @click="submitToggleActive">
              Confirm
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="snackbar.timeout" bottom right>
        {{ snackbar.message }}
        <template v-slot:actions>
          <v-btn color="white" variant="text" @click="snackbar.show = false">Close</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </template>
  
  <script setup>
  import { ref, onMounted, reactive, nextTick, computed } from 'vue';//, computed
  import AdminService from '@/services/AdminService';
  import { useAuthStore } from '@/stores/auth';
  import apiClient from '@/services/api';
  import CompanyService from '@/services/CompaniesService';
  import CountriesService from '@/services/CountriesService';
  
  const authStore = useAuthStore();
  const companies = ref([]);
  const countries = ref([]);
  const search = ref('');
  const tableLoading = ref(true);
  const users = ref([]);
  const showPassword = ref(false);
  const showConfirmPassword = ref(false);

  const profileDialog = ref(false);
  const profileForm = ref(null);
  const isFormValid = ref(false);
  const dialogLoading = ref(false);
  const editDialog = ref(false);

  const editingUser = ref(null); // User object being edited
  const selectedUserRoles = ref([]); // Array of role names for the v-select
  const allAvailableRoles = ref([]); // Array of {name, description} from backend
  const loadingAllRoles = ref(false);
  const rolesForm = ref(null);
  const loadingCompanies = ref(false);

  const snackbar = ref({ show: false, message: '', color: 'success', timeout: 4000 });
  const showSnackbar = (message, color = 'success', timeout = 4000) => {
    snackbar.value = { message, color, timeout, show: true };
  };
  
  // const rules = {
  //   minOneRole: value => (value && value.length > 0) || 'User must have at least one role.',
  // };

  const rules = {
    minOneRole: value => (value && value.length > 0) || 'User must have at least one role.',
    required: value => !!value || 'This field is required.',
    email: value => /.+@.+\..+/.test(value) || 'E-mail must be valid.',
    minLength: len => value => (value && value.length >= len) || `Minimum ${len} characters.`,
    passwordMatch: computed(() => value => value === form.value.password || 'Passwords do not match.'),
  };

  const editrules = {
    required: value => !!value || 'This field is required.',
  };
  
  const headers = ref([
    { title: 'Email', key: 'email', sortable: true },
    { title: 'First Name', key: 'first_name', sortable: true },
    { title: 'Last Name', key: 'last_name', sortable: true },
    { title: 'Company', key: 'company_id', sortable: true, width: '200px' },
    { title: 'Roles', key: 'roles', sortable: false, width: '150px' },
    { title: 'Active', key: 'active', sortable: true, width: '100px' },
    { title: 'Country', key: 'country_code', sortable: true, width: '100px' },
    { title: 'Last Login', key: 'last_login_at', sortable: true, value: item => item.last_login_at ? new Date(item.last_login_at).toLocaleString() : 'N/A' },
    { title: 'Actions', key: 'actions', sortable: false, align: 'center', width: '160px' }
  ]);
  
  const fetchUsers = async () => {
    tableLoading.value = true;
    try {
      const response = await AdminService.getAllUsers();
      users.value = response.data;
    } catch (error) {
      showSnackbar('Failed to load users.', 'error');
      users.value = [];
    } finally {
      tableLoading.value = false;
    }
  };
  
  const fetchAllRoles = async () => {
    loadingAllRoles.value = true;
    try {
      const response = await AdminService.getAllRoles();
      allAvailableRoles.value = response.data; // Expects array of {id, name, description}
    } catch (error) {
      console.error("Error fetching roles:", error);
      showSnackbar('Failed to load available roles.', 'error');
    } finally {
      loadingAllRoles.value = false;
    }
  };

  const fetchCompanies = async () => {
    loadingCompanies.value = true;
    try {
      const response = await CompanyService.getCompanies();
      companies.value = response;
    } catch (error) {
      showSnackbar('Failed to load companies.', 'error');
      companies.value = [];
    } finally {
      loadingCompanies.value = false;
    }
  };

  const fetchCountries = async () => {

    const response = await CountriesService.getCountries();
    countries.value = response;
  };

  const pwdDlg     = reactive({ show:false, user:null, newPwd:'', valid:false });
  const disableDlg = reactive({ show:false, user:null });

  const openPasswordDlg = (user) => {
    Object.assign(pwdDlg,{show:true,user:user,newPwd:''});
  };

  const submitPassword = async () => {
    try {
      await apiClient.put(`/users/${pwdDlg.user.id}/password`, { password: pwdDlg.newPwd });
      showSnackbar('Password updated','success');  pwdDlg.show=false;
    } catch (error) {
      console.error("Error updating password:", error);
      showSnackbar(`Failed to update password: ${error.response?.data?.error || 'Server error'}`, 'error');
    }
  }

  // function confirmToggleActive(u){ Object.assign(disableDlg,{show:true,user:u}); }
  // async function submitToggleActive(){
  //   const uid = disableDlg.user.id;
  //   await apiClient.put(`/users/${uid}/active`, { active: !disableDlg.user.active });
  //   // refresh list (or mutate locally)
  //   await fetchUsers();
  //   showSnackbar('User status updated','success');  disableDlg.show=false;
  // }

  onMounted(() => {
    fetchUsers();
    fetchAllRoles();
    fetchCompanies();
    fetchCountries();
  });
  
  const openEditRolesDialog = (user) => {
    editingUser.value = { ...user };
    selectedUserRoles.value = user.roles ? [...user.roles] : []; // Current roles of the user
    editDialog.value = true;
  };
  
  const closeEditRolesDialog = () => {
    editDialog.value = false;
    editingUser.value = null;
    selectedUserRoles.value = [];
    rolesForm.value?.resetValidation();
  };
  
  const saveUserRoles = async () => {
    if (!editingUser.value || !rolesForm.value) return;
    const { valid } = await rolesForm.value.validate();
    if (!valid) {
        showSnackbar('Please ensure the user has at least one role.', 'warning');
        return;
    }
  
    dialogLoading.value = true;
    try {
      const response = await AdminService.updateUserRoles(editingUser.value.id, selectedUserRoles.value);
      // Update the user in the local list
      const index = users.value.findIndex(u => u.id === editingUser.value.id);
      if (index !== -1) {
        users.value[index] = response.data; // Backend should return updated user with new roles
      }
      showSnackbar(`Roles updated for ${editingUser.value.email}`, 'success');
      closeEditRolesDialog();
    } catch (error) {
      console.error("Error updating user roles:", error);
      showSnackbar(`Failed to update roles: ${error.response?.data?.error || 'Server error'}`, 'error');
    } finally {
      dialogLoading.value = false;
    }
  };

  const openProfileDialog = (user) => {
    editingUser.value = { ...user };
    profileDialog.value = true;
  };

  const saveProfile = async () => {
    if (!editingUser.value || !profileForm.value) return;
    const { valid } = await profileForm.value.validate();
    if (!valid) {
      showSnackbar('Please ensure the user has at least one role.', 'warning');
      return;
    }
    const { id, ...data } = editingUser.value;
    await AdminService.updateUserProfile(id, data);
    profileDialog.value = false;
    await fetchUsers();
    showSnackbar('Profile saved successfully!', 'success');
  }

  const addUserDialog = ref(false);
  const addUserForm = ref(null);
  const form     = ref({ id:null, email:'', first_name:'', last_name:'', company_id:'', active:true, password:'', confirmPassword:'' });

  function openAddUserDialog(item) {
    form.value = item ? { ...item } : { id:null, email:'', first_name:'', last_name:'', company_id:'', active:true };
    addUserDialog.value = true;
    nextTick(() => addUserForm.value?.resetValidation());
  }

  const handleAddUser = async () => {
    if (!addUserForm.value) return;
    const { valid } = await addUserForm.value.validate();
    if (!valid) {
      showSnackbar('Please correct the form errors.', 'warning');
      return;
    }

    const data = await authStore.registerUserAsAdmin({ email: form.value.email, first_name: form.value.first_name, last_name: form.value.last_name, active: form.value.active, password: form.value.password, company_id: form.value.company_id });
    if (data.error) {
      showSnackbar(data.error, 'error');
    } else {
      users.value.unshift(data);
      showSnackbar('User added successfully!', 'success');
      addUserDialog.value = false;
    }
  }
  
  function findName(list, id) {
    if (!list || !Array.isArray(list) || !id) return '�';
    const obj = list.find(i => i && i.id === id);
    return obj ? obj.name : `Unknown (ID: ${id})`;
  }

  function findCountryName(countryCode) {
    const country = countries.value.find(c => c.country_code === countryCode);
    return country ? country.country_name : countryCode;
  }

  function getRoleName(role) {
    return allAvailableRoles.value.find(r => r.name === role)?.readable_name || role;
  }
  </script>
  
  <style scoped>
  .v-card-title.tableheader {
    background-color: var(--v-theme-tableheader) !important;
    color: white !important;
  }
  .tableheader .v-icon { color: white !important; }
  .search-field .v-input__control .v-field { background-color: rgba(255, 255, 255, 0.9) !important; }
  .search-field label { color: rgba(0, 0, 0, 0.6) !important; }
  .text-disabled { color: rgba(0,0,0,0.38); }
  </style>
  